/**
 * @description Resolves a short link code and redirects to the target path.
 * Used for transactional email URLs (signup, invite, password reset).
 *
 * Key features:
 * - Fetches target path from backend /api/short-links/resolve/:shortCode
 * - Navigates to the resolved path within the SPA (no full page reload)
 * - Shows loading spinner during resolution, error on failure
 *
 * @dependencies
 * - React Router: useParams, useNavigate
 * - config: API_URL for backend calls
 *
 * @notes
 * - No auth required — these are clicked from emails by unauthenticated users
 * - Target path is always relative (backend enforces this to prevent open redirects)
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../services/api/config';

function ShortLinkRedirect() {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const resolve = async () => {
      try {
        const response = await fetch(
          `${config.API_URL}/short-links/resolve/${shortCode}`
        );

        if (!response.ok) {
          setError('This link is invalid or has expired.');
          return;
        }

        const data = await response.json();

        if (data.targetPath && data.targetPath.startsWith('/') && !data.targetPath.startsWith('//')) {
          // Navigate within the SPA — preserves query params
          navigate(data.targetPath, { replace: true });
        } else if (data.targetPath) {
          // Reject non-relative paths (open redirect prevention)
          setError('This link is invalid.');
        } else {
          setError('This link is invalid or has expired.');
        }
      } catch (err) {
        setError('Unable to resolve this link. Please try again.');
      }
    };

    if (shortCode) {
      resolve();
    }
  }, [shortCode, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Link Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Redirecting...</p>
      </div>
    </div>
  );
}

export default ShortLinkRedirect;
